import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { useSelector } from 'react-redux';
import {
  FormGroup,
  FormRadioGroup,
  Heading,
  InPageAlert,
  Input,
  Select,
  Textarea,
} from '@snsw/react-component-library';
import { radioOptionsYesNo, selectAustraliaStateOptions } from 'shared/constants/constants';
import { capitalizeFirstLetter, sanitizeHtml } from 'shared/utils/Helpers';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import styles from '../PlantDesignDetails.module.scss';

export const DesignAlterationDetails = React.forwardRef((props, ref) => {
  const savedAlterationDetails = useSelector((state) => state.plantDesign).alterationDetails;

  const [wasDesignedBefore, setWasDesignedBefore] = useState(savedAlterationDetails?.wasDesignedBefore);
  const [reasonForAlteration, setReasonForAlteration] = useState(savedAlterationDetails?.reasonForAlteration);
  const [registrationNumber, setRegistrationNumber] = useState(savedAlterationDetails?.registrationNumber);
  const [registeredLocation, setRegisteredLocation] = useState(savedAlterationDetails?.registeredLocation);

  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const PAGE_CONSTANTS = PlantDesignDetailsPage.designAlteration;

  const wasDesignedBeforeOptions = radioOptionsYesNo;
  const locationOptions = selectAustraliaStateOptions;
  const [wasDesignedBeforeLabel, setWasDesignedBeforeLabel] = useState('');

  useEffect(() => {
    props.setCanContinue(true);
    if (!registeredLocation || !locationOptions) return;

    if (registeredLocation !== 'NSW') {
      props.setCanContinue(false);
    }
  }, [registeredLocation]);

  useEffect(() => {
    setWasDesignedBeforeLabel(getWasDesignedBeforeLabel(props.plantRef.code, props.subType));
  }, [props.plantRef, props.subType]);

  useEffect(() => {
    props.onErrorChange(errorSummary);
  }, [errorSummary]);

  const validators = [
    {
      fieldName: 'wasDesignedBefore',
      fieldValue: wasDesignedBefore,
      isValid: (v) => !isEmpty(v) && v !== 'yes',
      errorMessage: (v) =>
        isEmpty(v)
          ? 'Please select an option'
          : sanitizeHtml(
              'This design was completed before current registration requirements took effect. Contact SafeWork NSW at <a href="mailto:engineering@safework.nsw.gov.au">engineering@safework.nsw.gov.au</a> to find out whether you can apply for an alteration or need to register a new design.',
            ),
    },
    {
      fieldName: 'reasonForAlteration',
      fieldValue: reasonForAlteration,
      isValid: (v) => !isEmpty(v) || wasDesignedBefore === 'yes',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'registrationNumber',
      fieldValue: registrationNumber,
      isValid: (v) => !isEmpty(v) || wasDesignedBefore === 'yes',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'registeredLocation',
      fieldValue: registeredLocation,
      isValid: (v) => !isEmpty(v) || wasDesignedBefore === 'yes',
      errorMessage: () => 'Please select an option',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isValid = validateExt(validators, setErrors, setErrorSummary);
        return isValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      wasDesignedBefore,
      reasonForAlteration,
      registrationNumber,
      registeredLocation,
    };

    props.onDetailsChange(details);

    if (props.showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [wasDesignedBefore, reasonForAlteration, registrationNumber, registeredLocation]);

  return (
    <>
      <Heading level={2} className={styles.plantTypeTitle}>
        {`${capitalizeFirstLetter(props.plantRef.name)} alteration details`}
      </Heading>

      <FormRadioGroup
        id="wasDesignedBefore"
        name="wasDesignedBefore"
        vertical={true}
        legend={wasDesignedBeforeLabel}
        options={wasDesignedBeforeOptions}
        onChange={setWasDesignedBefore}
        value={wasDesignedBefore}
        errorMessage={errors?.wasDesignedBefore?.errorMessage}
        hasError={(props.showErrors || wasDesignedBefore === 'yes') && errors?.wasDesignedBefore?.invalid}
      />

      {wasDesignedBefore === 'no' && (
        <>
          <FormGroup
            id="reasonForAlteration"
            label={PAGE_CONSTANTS.reasonForAlteration.label}
            helpMessage={PAGE_CONSTANTS.reasonForAlterationHelpMessage}
            errorMessage={errors?.reasonForAlteration?.errorMessage}
            hasError={props.showErrors && errors?.reasonForAlteration?.invalid}
            margin={{ top: 'lg' }}
          >
            <Textarea
              value={reasonForAlteration}
              onChange={(e) => setReasonForAlteration(e.target.value)}
              maxLength={2000}
            />
          </FormGroup>

          <FormGroup
            id="registrationNumber"
            label={PAGE_CONSTANTS.registrationNumber.label}
            errorMessage={errors?.registrationNumber?.errorMessage}
            hasError={props.showErrors && errors?.registrationNumber?.invalid}
            margin={{ top: 'lg' }}
          >
            <Input
              value={registrationNumber || ''}
              onChange={(e) => setRegistrationNumber(e.target.value)}
              inputWidth="xl"
              maxLength={250}
            />
          </FormGroup>

          <FormGroup
            id="registeredLocation"
            label={PAGE_CONSTANTS.registeredLocation.label}
            helpMessage={PAGE_CONSTANTS.registeredLocationHelpMessage}
            errorMessage={errors?.registeredLocation?.errorMessage}
            hasError={props.showErrors && errors?.registeredLocation?.invalid}
          >
            <Select
              value={registeredLocation}
              options={locationOptions}
              onChange={(e) => setRegisteredLocation(e.target.value)}
              inputWidth="md"
            />
          </FormGroup>

          {registeredLocation && registeredLocation !== 'NSW' && (
            <InPageAlert
              variant="warning"
              title=" Please contact the Work health and safety regulator for that state to discuss alteration options."
              headingLevel="h5"
              className={styles.alteratioinWarning}
            />
          )}
        </>
      )}
    </>
  );
});

DesignAlterationDetails.displayName = 'PlantDesignDetailsCommon';

DesignAlterationDetails.propTypes = {
  plantType: PropTypes.string,
  subType: PropTypes.string,
  plantRef: PropTypes.object,
  onDetailsChange: PropTypes.func,
  onErrorChange: PropTypes.func,
  showErrors: PropTypes.bool,
  setCanContinue: PropTypes.func,
};

export const getWasDesignedBeforeLabel = (plantTypeCode, plantSubType) => {
  const PLANT_REFS = PlantDesignRefData.plantRefs;

  if (plantTypeCode === PLANT_REFS.concretePlacingBoom.code) {
    if (plantSubType === PLANT_REFS.concretePlacingBoom.subTypeRef.satelliteOrFixed)
      return 'Was the original plant designed before 13 February 2015?';
    else if (plantSubType === PLANT_REFS.concretePlacingBoom.subTypeRef.mobileOrTruckMounted)
      return 'Was the original plant designed before 1 January 2012?';
  } else if (plantTypeCode === PLANT_REFS.passengerRopeway.code) {
    return 'Was the original plant designed before 13 February 2015?';
  }

  return 'Was the original plant designed before 1 September 2001?';
};
